import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { AuthenticationService, CurrentUser } from '../domain';
import { ConsoleService } from '../widgets/console.service';

/**
 * Blocks the review area while a PPE admin is proxied as another user, unless the
 * proxying admin is an Academic HR admin.
 *
 * Runs after AuthGuardService (the parent route's guard), so the identity has already
 * been resolved by the time this executes. When blocked, the user is redirected to the
 * spoofed user's home area instead.
 */
@Injectable()
export class NoProxyGuardService implements CanActivate {

	constructor(
		private authSvc: AuthenticationService,
		private router: Router,
		private snackbar: MatSnackBar,
		private console: ConsoleService
	) {}

	canActivate(): Promise<boolean | UrlTree> {
		return firstValueFrom(this.authSvc.currentIdentity).then(identity =>
			identity.match<boolean | UrlTree>(
				() => true, // not authenticated: let the normal auth flow handle it
				i => {
					if (i.user.canAccessReview)
						return true;
					this.console.info('[NoProxyGuard] blocked: session is proxied by a non-AHR admin');
					this.snackbar.open('This area is not available while proxied as another user.', null, { duration: 5000 });
					return this.router.parseUrl(NoProxyGuardService.homeFor(i.user));
				}));
	}

	/** Mirrors AuthenticationService.sendUserOff, minus the review area. */
	private static homeFor(u: CurrentUser): string {
		if (u.roles.academicHumanResources) return '/ahr';
		if (u.roles.civilRightsCompliance) return '/crc';
		if (u.roles.academic.hasValue()) return '/academic';
		return '/explore';
	}
}
